/**
 * Integer lump noise for the swept brush (materials-perf-plan §2.3 / B4; large-volume plan §2.2).
 *
 * Contract. For a stroke with radius `rFx` and lump `lumpFx` (both 1/16-voxel fixed point) every cell
 * gets its own effective radius
 *
 *     rEff(cell) = rFx - lumpFx + round(lumpFx * f(cell) / 255),   f(cell) in [0, 255]
 *
 * and is included iff its distance to the primitive is <= rEff(cell) (the same inclusive rule as
 * `insideFx`, so lump 0 is bit-identical to the plain sweep). `f` depends only on (cell, seed): it is
 * value noise on a lattice of `cell` voxels (hash at the lattice corners, tri-linear interpolation in
 * integer arithmetic), so lumps are blobs about `cell` voxels wide rather than per-voxel speckle.
 *
 * Monotone in distance. For a fixed cell and seed, rEff is a constant, so `include(d)` is a threshold
 * on d: d' <= d and include(d) imply include(d'). That is what keeps the crescent split invariant
 * (rasterize.ts) true with lump > 0: dist(cell, capsule(prev, cur)) <= dist(cell, prev), so a cell in
 * the lumpy sphere(prev) is in the lumpy capsule, and capsule \ sphere(prev) is still the exact
 * increment. Nothing here uses floats except an exact integer division (see `floorDivExact`), so
 * replay is identical on every platform ("No float noise in bandInclude", plan §6).
 *
 * Band. rEff lies in [rFx - lumpFx, rFx], so cells closer than rFx - lumpFx are in for every noise
 * value and cells beyond rFx are out for every noise value; only the shell needs `f` (sweep.ts).
 */

/** Resolution of the lump field: `lumpField256` returns [0, LUMP_FIELD_MAX]. */
export const LUMP_FIELD_MAX = 255

/** 32-bit integer finaliser (lowbias32). Pure `Math.imul`, deterministic everywhere. */
export function hash32(value: number): number {
  let x = value >>> 0
  x ^= x >>> 16
  x = Math.imul(x, 0x7feb352d)
  x ^= x >>> 15
  x = Math.imul(x, 0x846ca68b)
  x ^= x >>> 16
  return x >>> 0
}

/**
 * Smooth 1D value noise in [-1, 1]: hashed values at whole `x`, eased between (smoothstep). Pure and
 * seeded, a hash and a few multiplies, so a drip can carry its own wander and sticky patches, or a
 * stroke its own wandering width, decided the same way every time.
 */
export function noise1(seed: number, x: number): number {
  const i = Math.floor(x)
  const f = x - i
  const a = hash32((Math.imul(i, 0x9e3779b1) ^ Math.imul(seed | 0, 0x85ebca6b)) >>> 0) / 2147483648 - 1
  const b = hash32((Math.imul(i + 1, 0x9e3779b1) ^ Math.imul(seed | 0, 0x85ebca6b)) >>> 0) / 2147483648 - 1
  return a + (b - a) * f * f * (3 - 2 * f)
}

/** Lattice corner value in [0, 255] for integer lattice coordinates and a seed. */
export function lumpLattice(ix: number, iy: number, iz: number, seed: number): number {
  const h = hash32((Math.imul(ix | 0, 73856093) ^ Math.imul(iy | 0, 19349663) ^ Math.imul(iz | 0, 83492791) ^ (seed | 0)) >>> 0)
  return (h >>> 8) & LUMP_FIELD_MAX
}

/** floor(a / b) for integers, b > 0 (handles negative a). */
export function floorDiv(a: number, b: number): number {
  const q = Math.trunc(a / b)
  return a - q * b < 0 ? q - 1 : q
}

/**
 * floor(n / d) for non-negative integers n < 2^31 and 0 < d <= 2^14. The double quotient is correctly
 * rounded and the true quotient is at least 1/d away from the next integer, so flooring it is exact.
 */
function floorDivExact(n: number, d: number): number {
  return (n / d) | 0
}

/**
 * The lump field at cell (x, y, z): value noise on a lattice of `cell` voxels, in [0, 255].
 * Reference implementation (8 lattice hashes per call); `LumpRowNoise` is the incremental version
 * the sweep uses and must agree with this exactly.
 */
export function lumpField256(x: number, y: number, z: number, cell: number, seed: number): number {
  const c = cell | 0
  if (c <= 1) return lumpLattice(x, y, z, seed)
  const ix = floorDiv(x, c), iy = floorDiv(y, c), iz = floorDiv(z, c)
  const tx = x - ix * c, ty = y - iy * c, tz = z - iz * c
  const wy0 = c - ty, wy1 = ty, wz0 = c - tz, wz1 = tz
  const plane = (px: number) =>
    lumpLattice(px, iy, iz, seed) * wy0 * wz0
    + lumpLattice(px, iy + 1, iz, seed) * wy1 * wz0
    + lumpLattice(px, iy, iz + 1, seed) * wy0 * wz1
    + lumpLattice(px, iy + 1, iz + 1, seed) * wy1 * wz1
  const n = plane(ix) * (c - tx) + plane(ix + 1) * tx
  return floorDivExact(n, c * c * c)
}

/** The effective radius of a cell whose lump field value is `f256`. In [rFx - lumpFx, rFx]. */
export function lumpRadiusFx(rFx: number, lumpFx: number, f256: number): number {
  // round-half-up of lumpFx * f / 255 in integers: exactly 0 at f = 0 and exactly lumpFx at f = 255.
  return rFx - lumpFx + floorDivExact(lumpFx * f256 + 127, LUMP_FIELD_MAX)
}

/**
 * The banded inclusion test: a cell at squared distance `d2Fx` (fixed point squared) from the
 * primitive, with lump field value `f256`, is inside iff d2 <= rEff^2. Monotone in d by construction.
 */
export function bandInclude(d2Fx: number, rFx: number, lumpFx: number, f256: number): boolean {
  const r = lumpRadiusFx(rFx, lumpFx, f256)
  return r >= 0 && d2Fx <= r * r
}

/** Largest lump (fixed point) a brush of radius `rFx` can carry: the lump never exceeds the radius. */
export function clampLumpFx(lumpFx: number, rFx: number): number {
  const l = Math.max(0, Math.round(lumpFx))
  return l > rFx ? rFx : l
}

/**
 * Incremental lump field along one row (fixed y, z): the four lattice corners of each x-plane are
 * combined once per lattice column and reused for every cell in it. Walking x in increasing order
 * costs one lerp per cell plus four hashes per `cell` voxels; a jump recomputes both planes.
 * `at(x)` returns exactly `lumpField256(x, y, z, cell, seed)`.
 */
export class LumpRowNoise {
  private readonly c: number
  private readonly c3: number
  private iy = 0
  private iz = 0
  private wy0 = 0
  private wy1 = 0
  private wz0 = 0
  private wz1 = 0
  private ix = Number.NaN
  private plane0 = 0
  private plane1 = 0

  constructor(cell: number, readonly seed: number) {
    this.c = Math.max(1, cell | 0)
    this.c3 = this.c * this.c * this.c
  }

  get cell(): number { return this.c }

  beginRow(y: number, z: number): void {
    const c = this.c
    this.iy = floorDiv(y, c)
    this.iz = floorDiv(z, c)
    const ty = y - this.iy * c, tz = z - this.iz * c
    this.wy0 = c - ty; this.wy1 = ty
    this.wz0 = c - tz; this.wz1 = tz
    this.ix = Number.NaN
  }

  private plane(px: number): number {
    const iy = this.iy, iz = this.iz, seed = this.seed
    return lumpLattice(px, iy, iz, seed) * this.wy0 * this.wz0
      + lumpLattice(px, iy + 1, iz, seed) * this.wy1 * this.wz0
      + lumpLattice(px, iy, iz + 1, seed) * this.wy0 * this.wz1
      + lumpLattice(px, iy + 1, iz + 1, seed) * this.wy1 * this.wz1
  }

  at(x: number): number {
    const c = this.c
    if (c === 1) return lumpLattice(x, this.iy, this.iz, this.seed)
    const ix = floorDiv(x, c)
    if (ix !== this.ix) {
      if (ix === this.ix + 1) {
        this.plane0 = this.plane1
        this.plane1 = this.plane(ix + 1)
      } else {
        this.plane0 = this.plane(ix)
        this.plane1 = this.plane(ix + 1)
      }
      this.ix = ix
    }
    const tx = x - ix * c
    return floorDivExact(this.plane0 * (c - tx) + this.plane1 * tx, this.c3)
  }
}
